/**
 * RuleConfigFields - Dynamic configuration fields for different rule types
 *
 * Renders appropriate input fields based on the selected rule type.
 * Each rule type with hooks is extracted to its own component to comply with
 * React's Rules of Hooks (hooks must be called at the top level, not conditionally).
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Search, Loader2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDatasets } from '@/hooks/useDatasets';
import type { RuleConfig, RuleType, PathPatternConfig, DatasetNameConfig, TagConfig, ViewNameConfig } from '@/types/views';
import type { Dataset } from '@/types';
import { ViewSelector } from './ViewSelector';
import type { View, ViewListItem } from '@/types/views';
import { useViews } from '@/hooks/useViews';

interface RuleConfigFieldsProps {
  ruleType: RuleType;
  config: RuleConfig;
  onConfigChange: (config: RuleConfig) => void;
  /** View names to exclude from the ViewSelector (e.g. to prevent self-reference) */
  excludeViewNames?: string[];
  /** Called when the selected source view changes (for view_name rule type) */
  onSourceViewChange?: () => void;
}

// =============================================================================
// Sub-components for rule types that require hooks
// =============================================================================

interface PathPatternFieldsProps {
  config: PathPatternConfig;
  onConfigChange: (config: RuleConfig) => void;
}

/**
 * PathPatternFields - Extracted component for path_pattern rule type
 * Uses useState for input fields
 */
const PathPatternFields: React.FC<PathPatternFieldsProps> = ({ config, onConfigChange }) => {
  const [patternInput, setPatternInput] = React.useState('');
  const [excludeInput, setExcludeInput] = React.useState('');

  const updateConfig = (updates: Partial<PathPatternConfig>) => {
    onConfigChange({ ...config, ...updates } as RuleConfig);
  };

  const addPattern = () => {
    if (patternInput.trim()) {
      updateConfig({
        patterns: [...config.patterns, patternInput.trim()],
      });
      setPatternInput('');
    }
  };

  const removePattern = (index: number) => {
    updateConfig({
      patterns: config.patterns.filter((_, i) => i !== index),
    });
  };

  const addExclude = () => {
    if (excludeInput.trim()) {
      updateConfig({
        exclude_patterns: [...(config.exclude_patterns || []), excludeInput.trim()],
      });
      setExcludeInput('');
    }
  };

  const removeExclude = (index: number) => {
    updateConfig({
      exclude_patterns: (config.exclude_patterns || []).filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Include Patterns</Label>
        <div className="flex gap-2 mt-1">
          <Input
            placeholder="/data/sales/**/*.parquet"
            value={patternInput}
            onChange={(e) => setPatternInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPattern())}
          />
          <Button type="button" onClick={addPattern} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Glob patterns like /path/**/*.parquet</p>
        {config.patterns.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {config.patterns.map((pattern, index) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-1">
                {pattern}
                <button onClick={() => removePattern(index)} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label>Exclude Patterns (Optional)</Label>
        <div className="flex gap-2 mt-1">
          <Input
            placeholder="/data/sales/temp/*"
            value={excludeInput}
            onChange={(e) => setExcludeInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExclude())}
          />
          <Button type="button" onClick={addExclude} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {(config.exclude_patterns?.length || 0) > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {config.exclude_patterns!.map((pattern, index) => (
              <Badge key={index} variant="outline" className="flex items-center gap-1">
                {pattern}
                <button onClick={() => removeExclude(index)} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={config.case_sensitive || false}
          onCheckedChange={(checked) => updateConfig({ case_sensitive: checked })}
        />
        <Label>Case Sensitive</Label>
      </div>
    </div>
  );
};

interface DatasetNameFieldsProps {
  config: DatasetNameConfig;
  onConfigChange: (config: RuleConfig) => void;
}

/** Datasets requested per search. One small page keeps the picker responsive. */
const DATASET_PAGE_SIZE = 20;

/** Debounce applied to typing before a search request goes out, in ms. */
const DATASET_SEARCH_DEBOUNCE_MS = 300;

/**
 * DatasetNameFields - Extracted component for dataset_name rule type
 *
 * Search runs on the **server** (#1026). This used to call
 * `useDatasets({ fetchAll: true })` and filter in memory, which cost three cold
 * catalog reads / ~1.16MB / ~15s on a production catalog every time the
 * rule-config step mounted — the same cost #964 removed from the library page.
 */
const DatasetNameFields: React.FC<DatasetNameFieldsProps> = ({ config, onConfigChange }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), DATASET_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: datasetsResponse, isLoading, isError } = useDatasets({
    page: 1,
    size: DATASET_PAGE_SIZE,
    search: debouncedSearch || undefined,
  });

  // Safely extract datasets array with fallback
  const datasets = datasetsResponse?.items ?? [];
  const totalDatasets = datasetsResponse?.total ?? 0;

  // Filter out already selected datasets. The search itself is server-side, so
  // there is no client-side name filter left to apply.
  const availableDatasets = React.useMemo(() => {
    return datasets
      .filter((ds: Dataset) => !config.dataset_names.includes(ds.name))
      .sort((a: Dataset, b: Dataset) => a.name.localeCompare(b.name));
  }, [datasets, config.dataset_names]);

  const updateConfig = (updates: Partial<DatasetNameConfig>) => {
    onConfigChange({ ...config, ...updates } as RuleConfig);
  };

  const addDataset = (datasetName: string) => {
    if (!config.dataset_names.includes(datasetName)) {
      updateConfig({ dataset_names: [...config.dataset_names, datasetName] });
      setSearchQuery(''); // Clear search after adding
    }
  };

  const removeDataset = (datasetName: string) => {
    updateConfig({ dataset_names: config.dataset_names.filter((name) => name !== datasetName) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Search Datasets</Label>
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by dataset name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-4 text-muted-foreground">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Loading datasets...
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="text-sm text-destructive">
          Failed to load datasets. Please try again.
        </div>
      )}

      {/* Available Datasets List */}
      {!isLoading && !isError && (
        <>
          <div>
            <Label>Available Datasets</Label>
            <div className="mt-1 border rounded-md max-h-48 overflow-y-auto">
              {availableDatasets.length > 0 ? (
                <div className="p-1">
                  {availableDatasets.map((dataset: Dataset) => (
                    <button
                      key={dataset.id}
                      type="button"
                      onClick={() => addDataset(dataset.name)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm transition-colors flex items-center justify-between group"
                    >
                      <span className="font-medium">{dataset.name}</span>
                      <Plus className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {debouncedSearch ? 'No datasets match your search' : 'No datasets available'}
                </div>
              )}
            </div>
            {/* The list is one page, not the catalog — say so, or a dataset past
                the first page looks like it does not exist (#1026). */}
            {totalDatasets > datasets.length && (
              <p className="mt-1 text-xs text-muted-foreground">
                Showing {datasets.length} of {totalDatasets} — refine your search
              </p>
            )}
          </div>

          {/* Selected Datasets */}
          {config.dataset_names.length > 0 && (
            <div>
              <Label>Selected Datasets</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {config.dataset_names.map((name) => (
                  <Badge key={name} variant="secondary" className="flex items-center gap-1">
                    {name}
                    <button
                      type="button"
                      onClick={() => removeDataset(name)}
                      className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Alert variant="info" className="mt-2">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Files from any selected dataset will be included in this view.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Empty State - only when the catalog itself is empty. An empty page
              under an active search means "no match", not "no datasets" (#1026). */}
          {!isLoading &&
            !isError &&
            !debouncedSearch &&
            config.dataset_names.length === 0 &&
            datasets.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">
                No datasets available. Create a dataset first to use this rule type.
              </div>
            )}
        </>
      )}
    </div>
  );
};

interface TagFieldsProps {
  config: TagConfig;
  onConfigChange: (config: RuleConfig) => void;
}

/**
 * TagFields - Extracted component for tag rule type
 * Uses useState for tag input
 */
const TagFields: React.FC<TagFieldsProps> = ({ config, onConfigChange }) => {
  const [tagInput, setTagInput] = React.useState('');

  const updateConfig = (updates: Partial<TagConfig>) => {
    onConfigChange({ ...config, ...updates } as RuleConfig);
  };

  const addTag = () => {
    if (tagInput.trim() && !config.tags.includes(tagInput.trim())) {
      updateConfig({ tags: [...config.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (index: number) => {
    updateConfig({ tags: config.tags.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Tags</Label>
        <div className="flex gap-2 mt-1">
          <Input
            placeholder="Enter tag name"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
          />
          <Button type="button" onClick={addTag} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {config.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {config.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-1">
                {tag}
                <button onClick={() => removeTag(index)} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={config.match_all || false}
          onCheckedChange={(checked) => updateConfig({ match_all: checked })}
        />
        <Label>Match All Tags</Label>
        <p className="text-xs text-muted-foreground ml-auto">
          {config.match_all ? 'Files must have ALL tags' : 'Files must have ANY tag'}
        </p>
      </div>
    </div>
  );
};

interface ViewNameFieldsProps {
  config: ViewNameConfig;
  onConfigChange: (config: RuleConfig) => void;
  excludeViewNames?: string[];
  /** Called when the selected source view changes */
  onSourceViewChange?: () => void;
}

/**
 * ViewNameFields - Extracted component for view_name rule type
 *
 * Uses useState to track the selected view ID separately from the view name
 * stored in config. ViewSelector matches selections by view ID internally,
 * but the rule config persists the view name (for backend compatibility).
 */
const ViewNameFields: React.FC<ViewNameFieldsProps> = ({ config, onConfigChange, excludeViewNames, onSourceViewChange }) => {
  // Track the selected view ID locally so ViewSelector can highlight the selection.
  // The config only stores the view *name*, but ViewSelector resolves by *id*.
  const [selectedViewId, setSelectedViewId] = React.useState<string | undefined>(undefined);

  // Fetch views so we can resolve an existing view name to its ID on edit.
  // `views` is mirrored below in a render-phase guard (not just read in JSX),
  // so it must be a STABLE reference: `viewsResponse?.items || []` would mint
  // a fresh `[]` on every render while the query is pending (viewsResponse
  // undefined), making the guard's `!==` check true forever and looping
  // (#1123 fix-loop round 1 — "Maximum update depth exceeded"). useMemo keyed
  // on `viewsResponse` keeps the same array reference across renders where
  // the query result hasn't changed.
  const { data: viewsResponse } = useViews({ status: 'active', size: 100 });
  const views = React.useMemo(() => viewsResponse?.items || [], [viewsResponse]);

  // When editing an existing view_name rule, resolve the stored name to a view ID
  // so the ViewSelector dropdown shows the correct selection. Converted from a
  // useEffect to a guarded render-time adjustment (react-hooks/set-state-in-effect):
  // mirrors the previous `config.view_name` / `views` pair in state so the lookup
  // only re-runs on an actual change, converging in one extra render. The original
  // effect's `selectedViewId` dependency is intentionally not mirrored here — it
  // only ever re-triggered a no-op re-check (the `!selectedViewId` guard below reads
  // the live value), since `selectedViewId` is otherwise only set together with a
  // `config.view_name` change via `handleViewSelect`.
  const [prevResolveViewName, setPrevResolveViewName] = React.useState(config.view_name);
  const [prevResolveViews, setPrevResolveViews] = React.useState(views);
  if (config.view_name !== prevResolveViewName || views !== prevResolveViews) {
    setPrevResolveViewName(config.view_name);
    setPrevResolveViews(views);
    if (config.view_name && !selectedViewId && views.length > 0) {
      const match = views.find((v) => v.name === config.view_name);
      if (match) {
        setSelectedViewId(match.id);
      }
    }
  }

  const handleViewSelect = (view: View | ViewListItem) => {
    const previousViewName = config.view_name;
    setSelectedViewId(view.id);
    onConfigChange({ ...config, view_name: view.name } as ViewNameConfig);
    // Notify parent to clear column configs when the source view changes
    if (previousViewName && previousViewName !== view.name) {
      onSourceViewChange?.();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Source View</Label>
        <ViewSelector
          selectedViewId={selectedViewId}
          onSelect={handleViewSelect}
          excludeViewNames={excludeViewNames}
        />
        {!config.view_name && (
          <p className="text-xs text-destructive mt-2">
            Please select a view
          </p>
        )}
        {config.view_name && (
          <p className="text-xs text-muted-foreground mt-2">
            This view will use the data from view: <strong>{config.view_name}</strong>
          </p>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Main RuleConfigFields Component
// =============================================================================

export const RuleConfigFields: React.FC<RuleConfigFieldsProps> = ({
  ruleType,
  config,
  onConfigChange,
  excludeViewNames,
  onSourceViewChange,
}) => {
  // Helper to update config (for rule types without hooks)
  const updateConfig = (updates: Partial<RuleConfig>) => {
    onConfigChange({ ...config, ...updates } as RuleConfig);
  };

  // Path Pattern Config - delegated to sub-component
  if (ruleType === 'path_pattern' && config.type === 'path_pattern') {
    return <PathPatternFields config={config} onConfigChange={onConfigChange} />;
  }

  // Source ID Config
  if (ruleType === 'source_id' && config.type === 'source_id') {
    return (
      <div className="space-y-4">
        <div>
          <Label>Source IDs</Label>
          <Input
            placeholder="Enter comma-separated source IDs"
            value={config.source_ids.join(', ')}
            onChange={(e) => {
              const ids = e.target.value.split(',').map((id) => id.trim()).filter(Boolean);
              updateConfig({ source_ids: ids });
            }}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Select which data sources to include
          </p>
        </div>
      </div>
    );
  }

  // Dataset ID Config
  if (ruleType === 'dataset_id' && config.type === 'dataset_id') {
    return (
      <div className="space-y-4">
        <div>
          <Label>Dataset IDs</Label>
          <Input
            placeholder="Enter comma-separated dataset IDs"
            value={config.dataset_ids.join(', ')}
            onChange={(e) => {
              const ids = e.target.value.split(',').map((id) => id.trim()).filter(Boolean);
              updateConfig({ dataset_ids: ids });
            }}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Select which datasets to include
          </p>
        </div>
      </div>
    );
  }

  // Dataset Name Config - delegated to sub-component
  if (ruleType === 'dataset_name' && config.type === 'dataset_name') {
    return <DatasetNameFields config={config} onConfigChange={onConfigChange} />;
  }

  // Metadata Config
  if (ruleType === 'metadata' && config.type === 'metadata') {
    return (
      <div className="space-y-4">
        <div>
          <Label>Field Path</Label>
          <Input
            placeholder="e.g., customer.region"
            value={config.field_path}
            onChange={(e) => updateConfig({ field_path: e.target.value })}
          />
        </div>
        <div>
          <Label>Operator</Label>
          <Select
            value={config.operator}
            onValueChange={(value) => updateConfig({ operator: value as 'equals' | 'not_equals' | 'contains' | 'regex' })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="equals">Equals</SelectItem>
              <SelectItem value="not_equals">Not Equals</SelectItem>
              <SelectItem value="contains">Contains</SelectItem>
              <SelectItem value="regex">Regex Match</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Value</Label>
          <Input
            placeholder="Enter value"
            value={String(config.value)}
            onChange={(e) => updateConfig({ value: e.target.value })}
          />
        </div>
      </div>
    );
  }

  // Date Range Config
  if (ruleType === 'date_range' && config.type === 'date_range') {
    return (
      <div className="space-y-4">
        <div>
          <Label>Start Date (Optional)</Label>
          <Input
            type="date"
            value={config.start_date || ''}
            onChange={(e) => updateConfig({ start_date: e.target.value })}
          />
        </div>
        <div>
          <Label>End Date (Optional)</Label>
          <Input
            type="date"
            value={config.end_date || ''}
            onChange={(e) => updateConfig({ end_date: e.target.value })}
          />
        </div>
        <div>
          <Label>Date Field</Label>
          <Input
            placeholder="e.g., created_at, modified_date"
            value={config.date_field}
            onChange={(e) => updateConfig({ date_field: e.target.value })}
          />
          <p className="text-xs text-muted-foreground mt-1">
            The metadata field containing the date to filter by
          </p>
        </div>
      </div>
    );
  }

  // File Size Config
  if (ruleType === 'file_size' && config.type === 'file_size') {
    return (
      <div className="space-y-4">
        <div>
          <Label>Minimum Size (bytes) - Optional</Label>
          <Input
            type="number"
            placeholder="0"
            value={config.min_size_bytes || ''}
            onChange={(e) => updateConfig({ min_size_bytes: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>
        <div>
          <Label>Maximum Size (bytes) - Optional</Label>
          <Input
            type="number"
            placeholder="No limit"
            value={config.max_size_bytes || ''}
            onChange={(e) => updateConfig({ max_size_bytes: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Filter files by size range. 1 MB = 1,048,576 bytes
        </p>
      </div>
    );
  }

  // Tag Config - delegated to sub-component
  if (ruleType === 'tag' && config.type === 'tag') {
    return <TagFields config={config} onConfigChange={onConfigChange} />;
  }

  // View Name Config - delegated to sub-component
  if (ruleType === 'view_name' && config.type === 'view_name') {
    return (
      <ViewNameFields
        config={config}
        onConfigChange={onConfigChange}
        excludeViewNames={excludeViewNames}
        onSourceViewChange={onSourceViewChange}
      />
    );
  }

  return null;
};
